import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import { getClientTransport } from '../handlers/ClientTransportHandler';

export interface Peer {
  machine_id: string;
  machine_name: string;
  tunnel_url: string;
  app_version: string;
  last_heartbeat: string;
  is_self: boolean;
}

export interface PeerNetworkState {
  peers: Peer[];
  remotePeers: Peer[];
  selfOnline: boolean;
  peerMachineIds: string;
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
}

const POLL_INTERVAL = 30_000;

function getLocalMachineId(): string | null {
  try {
    return localStorage.getItem('maestro_machine_id');
  } catch {
    return null;
  }
}

export default function usePeerNetwork(): PeerNetworkState {
  const [peers, setPeers] = useState<Peer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  const fetchPeers = useCallback(async () => {
    try {
      const data = await api.get('/api/peers/online');
      if (!mountedRef.current) return;

      const myMachineId = getLocalMachineId();
      const enriched: Peer[] = (data?.peers || []).map((p: any) => ({
        ...p,
        is_self: !!myMachineId && p.machine_id === myMachineId,
      }));

      setPeers(enriched);
      setError(null);
    } catch (e: any) {
      if (!mountedRef.current) return;
      setError(e?.message || 'Failed to fetch peers');
    } finally {
      if (mountedRef.current) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    fetchPeers();

    const interval = setInterval(fetchPeers, POLL_INTERVAL);

    const transport = getClientTransport();
    const unsub = transport.onEvent((msg: any) => {
      const eventType =
        msg?.payload?.event || msg?.payload?.type || msg?.handler;
      if (eventType === 'PEERS_UPDATED' || msg?.handler === 'peers') {
        fetchPeers();
      }
    });

    return () => {
      mountedRef.current = false;
      clearInterval(interval);
      unsub();
    };
  }, [fetchPeers]);

  const selfOnline = peers.some((p) => p.is_self);
  const remotePeers = peers.filter((p) => !p.is_self);
  const peerMachineIds = peers.map((p) => p.machine_id).sort().join(',');

  return {
    peers,
    remotePeers,
    selfOnline,
    peerMachineIds,
    isLoading,
    error,
    refresh: fetchPeers,
  };
}

// Standalone register/unregister for use outside React components
export async function registerPeer(body: {
  machine_id: string;
  machine_name: string;
  tunnel_url: string;
  app_version?: string;
}): Promise<void> {
  await api.post('/api/peers/register', body);
}

export async function unregisterPeer(machineId: string): Promise<void> {
  const res = await api.raw('/api/peers/unregister', {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ machine_id: machineId }),
  });
  if (!res.ok) throw new Error(`Unregister failed: ${res.status}`);
}
